<template>
  <responsive-drawer-header :color="theme.appBar.other.color" :moduleUnreadCount="unreadCount">
    <template v-slot:drawer-module-title>{{ mx_moduleTitle }}</template>
    <template v-slot:drawer-subtitle>{{ $t('documents') }}</template>

    <!-- left container -->
    <template v-slot:left-module-title>{{ mx_moduleTitle }}</template>
    <template v-slot:left-subtitle>
      <div class="op-text-sm" v-if="isLoaded && isDocumentDisplayed && displayedDocument">{{ displayedDocument.title }}</div>
    </template>

    <template v-slot:right>
      <!-- right content in case of document mode -->
      <div v-if="isLoaded" class="d-flex align-center align-self-stretch py-1">
        <v-divider :class="`${theme.appBar.other.bg} lighten-1 mx-2`" vertical title="0"></v-divider>

        <acknowledge-button
          v-if="lgAndUp && isToAcknowledge && displayedDocument"
          :document="displayedDocument"
          :documentTitle="displayedDocument.title"
          :moduleType="moduleType"
          :color="color"
          :small="false"
          noWrap
        ></acknowledge-button>

        <v-divider v-if="lgAndUp && isToAcknowledge && displayedDocument" :class="`${theme.appBar.other.bg} lighten-1 mx-2`" vertical title="1"></v-divider>

        <favorite-icon
          v-if="lgAndUp && displayedDocument && isDocumentDisplayed"
          :item="displayedDocument"
          :small="false"
          :color="color"
          noWrap
        ></favorite-icon>

        <v-divider
          v-if="lgAndUp && displayedDocument && isDocumentDisplayed"
          :class="`${theme.appBar.other.bg} lighten-1 ml-2 mr-1`"
          vertical
          title="2"
        ></v-divider>

        <v-menu v-if="hasMenu" bottom :max-width="400" left transition="slide-y-transition" offset-y close-on-click close-on-content-click>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :color="color" fab :small="xs" class="d-flex trx-15" icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list class="py-0" dense>
            <v-list-item v-if="hasTourMenu && dateYYYYMMDD">
              <v-list-item-icon>
                <v-icon>mdi-calendar-range</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <div class="d-flex align-center mr-3 pb-1">
                  <div v-if="formattedDate">Datum wählen {{ formattedDate }}&nbsp;</div>
                  <div class="d-flex justify-start justify-center">
                    <v-btn small class="d-flex" :disabled="!hasPreviousDay" icon @click.stop.prevent="moveDays(-1)">
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn small class="d-flex" :disabled="!hasNextDay" icon @click.stop.prevent="moveDays(1)">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-list-item-title>
            </v-list-item>

            <v-list-item v-if="hasTourMenu && !dateYYYYMMDD && (isTourRoute || isHashRoute)" @click="selectToday">
              <v-list-item-icon>
                <v-icon>mdi-calendar</v-icon>
              </v-list-item-icon>
              <v-list-item-title> Heute wählen</v-list-item-title>
            </v-list-item>

            <v-list-item v-if="hasTourMenu && isFollowTimeVisible && isTodaySelected" @click="autoScrollToCurrentTime = !autoScrollToCurrentTime">
              <v-list-item-icon>
                <v-icon>mdi-clock</v-icon>
              </v-list-item-icon>
              <v-list-item-title> {{ $t('fahrdienst452.followTimeDescription') }}</v-list-item-title>
            </v-list-item>

            <!-- switch back to document -->
            <v-list-item v-if="smAndDown && isDocumentSwitchVisible" @click="navigateToDocument">
              <v-list-item-icon>
                <v-icon>mdi-file-document</v-icon>
              </v-list-item-icon>
              <v-list-item-title> {{ $t('fd.displayDocument') }}</v-list-item-title>
            </v-list-item>

            <!-- switch back to tour -->
            <v-list-item v-if="hasTourMenu && isTourSwitchVisible" @click="navigateToTour">
              <v-list-item-icon>
                <v-icon>mdi-vector-polyline</v-icon>
              </v-list-item-icon>
              <v-list-item-title v-if="isTourRoute"> {{ $t('fahrdienst452.displayTour') }}</v-list-item-title>
              <v-list-item-title v-else> {{ $t('fahrdienst452.displayUmlauf') }}</v-list-item-title>
            </v-list-item>

            <toggle-read-button v-if="!isToAcknowledge && isDocumentDisplayed" listItem :color="color" :item="displayedDocument"></toggle-read-button>

            <download
              v-if="isDocumentDisplayed && features.enableDocumentDownload !== false"
              listItem
              :downloadUrl="displayedDocumentUrl"
              :color="color"
            ></download>

            <favorite-icon v-if="mdAndDown & isDocumentDisplayed" listItem :item="displayedDocument"></favorite-icon>

            <acknowledge-button
              listItem
              v-if="mdAndDown && isDocumentDisplayed && isToAcknowledge && displayedDocument"
              :documentTitle="displayedDocument.title"
              :document="displayedDocument"
              :moduleType="moduleType"
            ></acknowledge-button>

            <share-button v-if="isDocumentDisplayed" listItem :file="displayedDocument" :moduleBackendId="moduleBackendId" :color="color"></share-button>

            <v-list-item v-if="mx_needsDrawer && !mx_isLeftDrawerOpen" @click="mx_isLeftDrawerOpen = true">
              <v-list-item-icon>
                <v-icon>mdi-file-document</v-icon>
              </v-list-item-icon>
              <v-list-item-title> {{ $t('fd.displayDocuments') }}</v-list-item-title>
            </v-list-item>

            <v-divider></v-divider>
          </v-list>
        </v-menu>

        <v-divider v-if="hasMenu" :class="`${theme.appBar.other.bg} lighten-1 ml-1 mr-2`" vertical title="3"></v-divider>

        <icon-text-btn
          v-if="isLoaded && !hasTourMenu && isFollowTimeVisible && isTodaySelected"
          @onClick="autoScrollToCurrentTime = !autoScrollToCurrentTime"
          :icon="`mdi-clock`"
          :text="$t(autoScrollToCurrentTime ? 'fahrdienst452.followTime' : 'fahrdienst452.doNotfollowTime')"
          :hint="$t('fahrdienst452.followTimeDescription')"
          :iconColor="color"
          :width="90"
          :small="false"
          noWrap
        ></icon-text-btn>

        <v-divider
          v-if="isLoaded && !hasTourMenu && isFollowTimeVisible && isTodaySelected"
          :class="`${theme.appBar.other.bg} lighten-1 mx-2`"
          vertical
          title="4"
        ></v-divider>

        <icon-text-btn
          v-if="isLoaded && !hasTourMenu && isDocumentSwitchVisible"
          @onClick="navigateToDocument"
          :icon="`mdi-file-document`"
          :text="$t('fd.displayDocument')"
          :hint="$t('fd.displayDocument')"
          :iconColor="color"
          :width="70"
          :small="false"
          noWrap
        ></icon-text-btn>

        <v-divider v-if="isLoaded && !hasTourMenu && isDocumentSwitchVisible" :class="`${theme.appBar.other.bg} lighten-1 mx-2`" vertical title="5"></v-divider>

        <icon-text-btn
          v-if="isLoaded && !hasTourMenu && isTourSwitchVisible"
          @onClick="navigateToTour"
          :icon="`mdi-vector-polyline`"
          :text="isTourRoute ? $t('fahrdienst452.displayTour') : $t('fahrdienst452.displayUmlauf')"
          :hint="isTourRoute ? $t('fahrdienst452.displayTour') : $t('fahrdienst452.displayUmlauf')"
          :iconColor="color"
          :width="70"
          :small="false"
          noWrap
        ></icon-text-btn>

        <v-divider v-if="isLoaded && !hasTourMenu && isTourSwitchVisible" :class="`${theme.appBar.other.bg} lighten-1 mx-2`" vertical title="6"></v-divider>

        <div v-if="isLoaded && !hasTourMenu && dateYYYYMMDD" class="d-flex flex-column pb-1">
          <div class="d-flex justify-center">
            <v-btn small :color="theme.appBar.other.color" class="d-flex" icon :disabled="!hasPreviousDay" @click="moveDays(-1)">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn small :color="theme.appBar.other.color" class="d-flex" icon :disabled="!hasNextDay" @click="moveDays(1)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div v-if="formattedDate" class="op-text-sm" style="text-align: center">{{ formattedDate }}&nbsp;</div>
        </div>

        <v-divider v-if="isLoaded && !hasTourMenu && dateYYYYMMDD" :class="`${theme.appBar.other.bg} lighten-1 mx-2`" vertical title="7"></v-divider>

        <icon-text-btn
          v-if="isLoaded && !hasTourMenu && !dateYYYYMMDD && (isBaseRoute || isTourRoute || isHashRoute)"
          :icon="'mdi-calendar'"
          :text="$t('fahrdienst452.selectToday')"
          :width="100"
          :iconColor="theme.appBar.other.color"
          @onClick="selectToday"
          :small="false"
          noWrap
        ></icon-text-btn>

        <v-divider
          v-if="isLoaded && !hasTourMenu && !dateYYYYMMDD && (isBaseRoute || isTourRoute || isHashRoute)"
          :class="`${theme.appBar.other.bg} lighten-1 mx-2`"
          vertical
          title="8"
        ></v-divider>

        <umlauf-linie-kurs-search
          ref="umlaufLinieKursSearch"
          v-if="isLoaded && showSelections && useUmlaufLinieKursSelektion"
          :tourHash="tourHash"
          :dateYYYYMMDD="dateYYYYMMDD"
          @onUmlaufSelected="onUmlaufSelected"
          :text="track ? `${$t('fahrdienst452.selectUmlauf')} (${track})` : null"
          :small="false"
          noWrap
        ></umlauf-linie-kurs-search>

        <date-line-track-matrix
          ref="dateLineTrackMatrix"
          v-if="isLoaded && showSelections && useLinieKursSelektion"
          color="background darken-1"
          v-model="selectedDateLineTrack"
          :dayLineTracks="dayLineTracks"
          :text="track ? `${$t('fahrdienst452.selectLinieKurs')} (${track})` : null"
          :small="false"
          :trackStartsWithLine="trackStartsWithLine"
          noWrap
        ></date-line-track-matrix>
      </div>
    </template>
  </responsive-drawer-header>
</template>

<script>
import {
  ROUTE_FAHRDIENST_TOUR_FILE,
  ROUTE_FAHRDIENST_TOUR_DATE,
  ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK,
  ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK_FILE,
  ROUTE_FAHRDIENST_TOUR_DATE_FILE,
  ROUTE_FAHRDIENST_TOUR_LINE_TRACK,
  ROUTE_FAHRDIENST_TOUR_LINE_TRACK_FILE,
  ROUTE_FAHRDIENST_HASH_FILE,
  ROUTE_FAHRDIENST_HASH_DATE,
  ROUTE_FAHRDIENST_HASH_DATE_TOUR,
  ROUTE_FAHRDIENST_HASH_DATE_TOUR_FILE,
  ROUTE_FAHRDIENST_HASH_DATE_FILE,
  ROUTE_FAHRDIENST_HASH_TOUR,
  ROUTE_FAHRDIENST_HASH_TOUR_FILE,
} from './routes';
import { mapState } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dateUtils from '@/common/dateUtils';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import ResponsiveDrawerHeader from '@/layouts/ResponsiveDrawerHeader.vue';
import DateLineTrackMatrix from '@/modules/common/DateLineTrackMatrix.vue';
import Download from '@//components/buttons/Download.vue';
import ShareButton from '@/components/buttons/ShareButton.vue';
import ToggleReadButton from '@/components/buttons/ToggleReadButton.vue';
import FavoriteIcon from '@/components/buttons/FavoriteIcon.vue';
import AcknowledgeButton from '@/components/buttons/AcknowledgeButton.vue';
import acknowledgeService from '@/services/acknowledgeService';
import IconTextBtn from '@/components/buttons/IconTextBtn';

import fahrdienst452Service from './fahrdienst452Service';
import UmlaufLinieKursSearch from './UmlaufLinieKursSearch.vue';
import fahrdienst452Mixin from '@/modules/fahrdienst452/fahrdienst452Mixin';

const ROOT = { root: true };

export default {
  name: 'Fahrdienst452Header',
  mixins: [common, configMixin, dateTimeMixin, fahrdienst452Mixin],
  components: {
    UmlaufLinieKursSearch,
    FavoriteIcon,
    ResponsiveDrawerHeader,
    DateLineTrackMatrix,
    IconTextBtn,
    Download,
    ShareButton,
    ToggleReadButton,
    AcknowledgeButton,
  },
  mounted() {
    console.debug('Mounted Fahrdienst452Header', this.routeName);
    this.showSelections = true;

    if (this.isBaseRoute) {
      const storedSelection = localStorage.getItem('fahrdienst452Selection');
      if (storedSelection && !this.moduleConfig.showPopupOnLoad) {
        this.loadSelection();
      } else {
        setTimeout(() => {
          if (!this.selectedUmlauf && this.$refs['umlaufLinieKursSearch']) {
            console.debug('open umlaufLinieKursSearch');
            this.$refs['umlaufLinieKursSearch'].onOpen();
          } else if (!this.line && !this.track && this.$refs['dateLineTrackMatrix']) {
            console.debug('open dateLineTrackMatrix');
            this.$refs['dateLineTrackMatrix'].onOpen();
          }
        }, 1000);
      }
    }

    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });

    if (this.moduleConfig.selectToday && !this.dateYYYYMMDD) {
      this.selectToday();
    }
  },
  data() {
    return {
      showSelections: false,
      selectedUmlauf: null,
      clientWidth: document.body.clientWidth,
    };
  },
  computed: {
    ...mapState('fahrdienst452', ['fahrdienst452FileById', 'displayMode', 'isLoaded', 'unreadCount', 'dayLineKursList', 'tracksInSelection']),
    hasDocumentMenu() {
      return this.displayedDocument && this.isDocumentDisplayed;
    },
    hasTourMenu() {
      return (this.isTourRoute || this.isHashRoute) && this.clientWidth <= 782;
    },
    hasMenu() {
      return this.hasDocumentMenu || this.hasTourMenu;
    },
    useUmlaufLinieKursSelektion() {
      return !!this.moduleConfig.useUmlaufLinieKursSelektion;
    },
    /**
     * Default is to use the linieKursSelektion (date, line, track matrix) if no other feature is enabled and linieKursSelektion is true or undefined
     */
    useLinieKursSelektion() {
      return !this.moduleConfig.useUmlaufLinieKursSelektion && (this.moduleConfig.linieKursSelektion || !this.moduleConfig.linieKursSelektion);
    },
    /**
     * Find out all track numbers (Kurse) which are involved within the current tour and return in the order of their occurrence.
     */
    concernedTrackNumbers() {
      return fahrdienst452Service.concernedTrackNumbers(this.tracksInSelection);
    },
    isToAcknowledge() {
      return acknowledgeService.isToAcknowledge(this.displayedDocument);
    },
    autoScrollToCurrentTime: {
      get() {
        return this.$store.state.fahrdienst452.autoScrollToCurrentTime;
      },
      set(value) {
        this.$store.commit('fahrdienst452/setAutoScrollToCurrentTime', value);
      },
    },
    formattedDate() {
      let date = this.$route.params.dateYYYYMMDD ? dateUtils.parseYYYYMMDD(this.$route.params.dateYYYYMMDD) : null;
      return date ? dateUtils.formatAsLocaleShortDate(date, this.$store.state.language.locale) : null;
    },
    isTodaySelected() {
      if (this.$route.params.dateYYYYMMDD && this.selectedDateLineTrack) {
        let day = this.selectedDateLineTrack.value.day;
        let today = new Date().toISOString();
        return dateUtils.isSameDateIsoString(day, today);
      }
      return false;
    },
    /**
     * Getter/Setter for selectedDateLineTrack.
     */
    selectedDateLineTrack: {
      get() {
        let day = this.dateYYYYMMDD ? dateUtils.reformatYYYYMMDDAsDateIsoString(this.dateYYYYMMDD) : null;
        console.debug('get: selectedDateLineTrack dateYYYYMMDD=', this.dateYYYYMMDD, day);
        return {
          value: {
            line: this.line,
            track: this.track,
            day,
          },
        };
      },
      set(value) {
        console.debug('selectedDateLineTrack', value);
        if (!value) return;
        let { line, track, day } = value.value;
        let dateYYYYMMDD = day ? dateUtils.reformatDateIsoStringAsYYYYMMDD(day) : null;
        let { line: line_, track: track_, dateYYYYMMDD: dateYYYYMMDD_ } = this.$route.params;
        if (line === line_ && track === track_ && dateYYYYMMDD === dateYYYYMMDD_) {
          console.debug('selectedDateLineTrack equals previous=> do not push route', {
            line,
            track,
            dateYYYYMMDD,
          });
        } else {
          if (dateYYYYMMDD && line && track) {
            this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK, params: { dateYYYYMMDD, line: String(line), track: String(track) } });
          }
        }
      },
    },
    /**
     * Computes a list of text/value items containing text for a combobox with date, line and track.
     * @returns {text, value} items
     */
    dayLineTracks() {
      let formatter;
      if (typeof Intl !== 'undefined') {
        formatter = new Intl.DateTimeFormat('de-DE', {
          month: '2-digit',
          year: 'numeric',
          day: '2-digit',
          timeZone: 'Europe/Berlin',
        });
      }
      const myDutyLineStr = this.$t('myDuty.line');
      const myDutyTrackStr = this.$t('myDuty.track');
      return this.dayLineKursList.map((i) => {
        let dateTime = Date.parse(i.day);
        let dateStr = typeof Intl !== 'undefined' ? formatter.format(new Date(dateTime)) : new Date(dateTime).toLocaleDateString();
        return {
          text: `${dateStr}, ${myDutyLineStr} ${i.linie} ${myDutyTrackStr} ${i.kurs}`,
          value: { day: i.day, dateTime, line: i.linie, track: i.kurs },
        };
      });
    },
    datesIsoStrings() {
      console.debug('compute datesIsoStrings');
      let dateTimes = {};
      this.dayLineTracks.forEach((d) => (dateTimes[d.value.dateTime] = d));
      return Object.keys(dateTimes)
        .sort()
        .map((d) => new Date(Number(d)).toISOString())
        .filter((d) => d !== undefined);
    },
    color() {
      return this.theme.appBar.other.color;
    },
    hasPreviousDay() {
      return this.hasTargetDay(this.datesIsoStrings, this.dateYYYYMMDD, -1);
    },
    hasNextDay() {
      return this.hasTargetDay(this.datesIsoStrings, this.dateYYYYMMDD, 1);
    },
    routeName() {
      return this.$route.name;
    },
    trackStartsWithLine() {
      return !!this.moduleConfig.trackStartsWithLine;
    },
  },
  methods: {
    onResize() {
      this.clientWidth = document.body.clientWidth;
    },
    selectToday() {
      const dateYYYYMMDD = dateUtils.formatDateAsYYYYMMDD(new Date());
      let { tourHash, line, track, documentId } = this.$route.params;
      if (this.isBaseRoute) {
        if (this.useLinieKursSelektion) {
          this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE, params: { dateYYYYMMDD } });
        }
        if (this.useUmlaufLinieKursSelektion) {
          this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE, params: { dateYYYYMMDD } });
        }
      }
      if (this.isTourRoute) {
        if (this.routeName === ROUTE_FAHRDIENST_TOUR_FILE) {
          this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_FILE, params: { dateYYYYMMDD, documentId } });
        }
        if (this.routeName === ROUTE_FAHRDIENST_TOUR_LINE_TRACK) {
          this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK, params: { dateYYYYMMDD, line, track: track } });
        }
        if (this.routeName === ROUTE_FAHRDIENST_TOUR_LINE_TRACK_FILE) {
          this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK_FILE, params: { dateYYYYMMDD, line, track, documentId } });
        }
      }
      if (this.isHashRoute) {
        if (this.routeName === ROUTE_FAHRDIENST_HASH_FILE) {
          this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_FILE, params: { dateYYYYMMDD, documentId } });
        }
        if (this.routeName === ROUTE_FAHRDIENST_HASH_TOUR) {
          this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_TOUR, params: { dateYYYYMMDD, tourHash } });
        }
        if (this.routeName === ROUTE_FAHRDIENST_HASH_TOUR_FILE) {
          this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_TOUR_FILE, params: { dateYYYYMMDD, tourHash, documentId } });
        }
      }
    },
    hasDay(datesIsoStrings, date) {
      let searchDay = date.toISOString().substring(0, 10);
      return datesIsoStrings.find((isoString) => isoString.indexOf(searchDay) > -1);
    },
    hasTargetDay(datesIsoString, dateYYYYMMDD, numberOfDaysToAdd) {
      if (dateYYYYMMDD) {
        let day = dateUtils.parseYYYYMMDD(dateYYYYMMDD);
        let targetDay = dateUtils.addDays(day, numberOfDaysToAdd);
        return this.hasDay(this.datesIsoStrings, targetDay);
      }
      return false;
    },
    /**
     * Navigates to the tour. Depending on the currently display state (document, another tour or nothing), the
     * correct route is chosen.
     */
    navigateToTour() {
      this.$router.push({ query: { display: 'tour' } });
    },
    /**
     * Navigates to the requested document. Depending on the currently display state (document, another tour or nothing), the
     * correct route is chosen.
     */
    navigateToDocument() {
      this.$router.push({ query: { display: 'doc' } });
    },
    onUmlaufSelected(params) {
      let { yyyymmdd, tourHash } = params;
      this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_TOUR, params: { dateYYYYMMDD: yyyymmdd, tourHash } });
    },
    markAsRead() {
      console.debug('mark as read');
      let sendboxTitle = this.$t('action.read.msg', { title: this.displayedDocument.title });
      let snackbarText = this.$t('action.document.read');
      this.$store.dispatch(
        `sendbox/markAsRead2`,
        {
          item: this.displayedDocument,
          sendboxTitle,
          snackbarText,
          immediate: true,
        },
        ROOT
      );
    },
    markAsUnread() {
      let sendboxTitle = this.$t('action.unread.msg', { title: this.displayedDocument.title });
      let snackbarText = this.$t('action.document.unread');
      this.$store.dispatch(
        `sendbox/markAsUnread2`,
        {
          item: this.displayedDocument,
          sendboxTitle,
          snackbarText,
        },
        ROOT
      );
    },
    moveDays(numberOfDaysToAdd) {
      console.debug('moveDays', numberOfDaysToAdd);
      if (this.dateYYYYMMDD) {
        let day = dateUtils.parseYYYYMMDD(this.dateYYYYMMDD);
        if (this.hasTargetDay(this.datesIsoStrings, this.dateYYYYMMDD, numberOfDaysToAdd)) {
          let targetDay = dateUtils.addDays(day, numberOfDaysToAdd);
          let newDay = dateUtils.formatDateAsYYYYMMDD(targetDay);
          let routeConfig = this.displayHint
            ? { name: this.routeName, params: { dateYYYYMMDD: newDay }, query: { display: this.displayHint } }
            : { name: this.routeName, params: { dateYYYYMMDD: newDay } };
          this.$router.push(routeConfig);
        }
      }
    },
    storeSelection() {
      localStorage.setItem(
        'fahrdienst452Selection',
        JSON.stringify({
          dateYYYYMMDD: this.dateYYYYMMDD ? this.dateYYYYMMDD : null,
          line: this.line ? this.line : null,
          track: this.track ? this.track : null,
          tourHash: this.tourHash ? this.tourHash : null,
          documentId: this.documentId ? this.documentId : null,
          displayHint: this.displayHint ? this.displayHint : null,
        })
      );
    },
    loadSelection() {
      const storedValue = localStorage.getItem('fahrdienst452Selection');
      if (storedValue) {
        let { line, track, tourHash, dateYYYYMMDD, documentId, displayHint } = JSON.parse(storedValue);
        if (this.useLinieKursSelektion) {
          if (dateYYYYMMDD && line && track) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK_FILE, params: { dateYYYYMMDD, line, track, documentId } });
              if (displayHint) {
                this.$router.push({ query: { display: displayHint } });
              }
            } else {
              this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK, params: { dateYYYYMMDD, line, track } });
            }
          }
          if (dateYYYYMMDD && !line && !track) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_FILE, params: { dateYYYYMMDD, documentId } });
              if (displayHint) {
                this.$router.push({ query: { display: displayHint } });
              }
            } else {
              this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE, params: { dateYYYYMMDD } });
            }
          }
          if (!dateYYYYMMDD && line && track) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_LINE_TRACK_FILE, params: { line, track, documentId } });
              if (displayHint) {
                this.$router.push({ query: { display: displayHint } });
              }
            } else {
              this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_LINE_TRACK, params: { line, track } });
            }
          }
          if (!dateYYYYMMDD && !line && !track) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_FILE, params: { documentId } });
            }
          }
        }
        if (this.useUmlaufLinieKursSelektion) {
          if (dateYYYYMMDD && tourHash) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_TOUR_FILE, params: { dateYYYYMMDD, tourHash, documentId } });
              if (displayHint) {
                this.$router.push({ query: { display: displayHint } });
              }
            } else {
              this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_TOUR, params: { dateYYYYMMDD, tourHash } });
            }
          }
          if (dateYYYYMMDD && !tourHash) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_FILE, params: { dateYYYYMMDD, documentId } });
              if (displayHint) {
                this.$router.push({ query: { display: displayHint } });
              }
            } else {
              this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE, params: { dateYYYYMMDD } });
            }
          }
          if (!dateYYYYMMDD && tourHash) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_HASH_TOUR_FILE, params: { tourHash, documentId } });
              if (displayHint) {
                this.$router.push({ query: { display: displayHint } });
              }
            } else {
              this.$router.push({ name: ROUTE_FAHRDIENST_HASH_TOUR, params: { tourHash } });
            }
          }
          if (!dateYYYYMMDD && !tourHash) {
            if (documentId) {
              this.$router.push({ name: ROUTE_FAHRDIENST_HASH_FILE, params: { documentId } });
            }
          }
        }
      }
    },
  },
  watch: {
    dateYYYYMMDD() {
      this.storeSelection();
    },
    line() {
      this.storeSelection();
    },
    track() {
      this.storeSelection();
    },
    tourHash() {
      this.storeSelection();
    },
    documentId() {
      this.storeSelection();
    },
    displayHint() {
      this.storeSelection();
    },
  },
};
</script>

<style type="text/css">
.content-left-container {
  margin-right: 0 !important;
}
</style>
